import { useState } from 'react';
import api from '../api';
import { useAuth, type ProfileUser } from '../state';

/**
 * One-time "Add your email" step for members who signed up by phone before an
 * email became required. Every account needs an address we can reach (account
 * notices, memo-ready emails, replies to feedback), so this shows until they
 * add one. New phone sign-ups give it during setup and never see this.
 */
export function AddEmailPrompt() {
  const profile = useAuth((s) => s.profile);
  const setProfile = useAuth((s) => s.setProfile);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const needsEmail =
    !!profile &&
    !!profile.profileCompletedAt &&
    !(profile.email ?? '').trim() &&
    !(profile.contactEmail ?? '').trim();
  if (!needsEmail) return null;

  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

  const save = async () => {
    if (!valid || saving) return;
    setSaving(true);
    setError(null);
    try {
      const res = await api.completeProfile({ contactEmail: value.trim() });
      setProfile({ ...profile, ...(res.user as Partial<ProfileUser>), contactEmail: value.trim().toLowerCase() } as ProfileUser);
    } catch (e) {
      setError((e as Error).message || 'Could not save your email. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div className="addemail-root" role="dialog" aria-modal="true" aria-labelledby="addemail-title">
      <div className="addemail-scrim" />
      <form
        className="addemail"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <h2 id="addemail-title" className="addemail__title">Add your email</h2>
        <p className="addemail__body">
          You signed in with your phone number. Add an email so we can send you account notices, tell you when a
          memo is ready, and reply when you send feedback. You'll still sign in with your phone.
        </p>
        <input
          className="addemail__input"
          type="email"
          inputMode="email"
          autoComplete="email"
          autoFocus
          placeholder="you@example.com"
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            if (error) setError(null);
          }}
        />
        <div className="addemail__err" aria-live="polite">
          {error ?? (value && !valid ? 'Enter a valid email address.' : '')}
        </div>
        <button type="submit" className="addemail__btn" disabled={!valid || saving}>
          {saving ? <span className="addemail__spin" aria-label="Saving" /> : 'Save email'}
        </button>
      </form>
      <style>{`
        .addemail-root { position: fixed; inset: 0; z-index: 1400; display: grid; place-items: center; padding: 16px; }
        .addemail-scrim { position: absolute; inset: 0; background: rgba(15,23,20,0.45); }
        .addemail { position: relative; width: min(440px, 100%); background: var(--surface-page); border-radius: 16px;
          padding: 28px 24px 24px; box-shadow: 0 20px 60px rgba(15,23,20,0.25); display: grid; gap: 12px; }
        .addemail__title { margin: 0; font: 500 24px/1.2 var(--f-serif); color: var(--ink); }
        .addemail__body { margin: 0; font: 400 14.5px/1.55 var(--f-sans); color: var(--text-decorative); }
        .addemail__input { height: 46px; padding: 0 14px; border: 1px solid var(--border-hairline); border-radius: 10px;
          background: #fff; font: 400 15px var(--f-sans); color: var(--ink); outline: none; }
        .addemail__input:focus { border-color: var(--heartwood); box-shadow: 0 0 0 3px rgba(27,58,46,0.12); }
        .addemail__err { min-height: 18px; font: 400 12.5px/1.4 var(--f-sans); color: var(--oxblood, #6B2030); }
        .addemail__btn { height: 44px; border: 0; border-radius: 999px; background: var(--heartwood); color: var(--parchment);
          font: 600 14.5px var(--f-sans); cursor: pointer; display: grid; place-items: center; }
        .addemail__btn:disabled { opacity: 0.55; cursor: default; }
        .addemail__spin { width: 16px; height: 16px; border-radius: 50%; border: 2px solid currentColor; border-top-color: transparent;
          animation: spinSlow 900ms linear infinite; }
      `}</style>
    </div>
  );
}
